import { type MouseEvent, type ReactElement } from 'react';

import { Box, styled } from '@mui/material';
import { useLocation } from 'react-router-dom';

import { useSidebar } from '../hooks';
import { type Route, type SidebarPermissionsLookup } from '../';

import { LinkListItem } from './link-list-item';
import { LinkStyledButton } from './link-styled-button';
import { MainMenuLink } from './main-menu-link';
import { MainMenuLinkTitle } from './main-menu-link-title';
import { MainMenuListContainer } from './main-menu-list-container';

const IconContainer = styled(Box)({
  height: 16,
});

export type MainMenuListProps = {
  routes: Route[];
  sidebarPermissionsLookup: SidebarPermissionsLookup;
};

export const MainMenuList = (props: MainMenuListProps): ReactElement => {
  const {
    shouldShowSubMenu,
    showSubMenu,
    updateSubMenu,
    hideSubMenu,
    subMenu,
  } = useSidebar();

  const { pathname } = useLocation();

  const handleClick =
    (route: Route) =>
    (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>): void => {
      event.stopPropagation();

      if (route.subroutes === undefined) {
        hideSubMenu();
        return;
      }

      event.preventDefault();

      if (subMenu?.subroutes === route.subroutes) {
        hideSubMenu();
        return;
      }

      if (!shouldShowSubMenu) {
        showSubMenu({
          parentRoutePath: route.path,
          parentRouteName: route.name,
          subroutes: route.subroutes,
        });
      }

      updateSubMenu({
        parentRoutePath: route.path,
        parentRouteName: route.name,
        subroutes: route.subroutes,
      });
    };

  return (
    <MainMenuListContainer>
      {props.routes.map((route) => {
        const current = pathname.split('/')[1];
        const reference = route.path.slice(1);
        const isActive = current === reference;

        const permissionState = props.sidebarPermissionsLookup?.[route.path];

        if (permissionState === undefined)
          throw new Error('Incorrect or incomplete route lookup.');

        if (permissionState.isHidden) return null;

        const iconContainerProp = route?.iconContainerProp
          ? route.iconContainerProp
          : null;

        return (
          <LinkListItem
            key={route.path}
            isDisabled={
              permissionState.isDisabled || !permissionState.isSubscribed
            }
          >
            {route.subroutes === undefined ? (
              <MainMenuLink
                isActive={Boolean(isActive)}
                onClick={handleClick(route)}
                to={route.path}
              >
                <IconContainer {...iconContainerProp}>
                  <route.Icon />
                </IconContainer>
                <MainMenuLinkTitle
                  isDisabled={permissionState.isDisabled}
                  isSubscribed={permissionState.isSubscribed}
                >
                  {route.name}
                </MainMenuLinkTitle>
              </MainMenuLink>
            ) : (
              <LinkStyledButton
                isActive={Boolean(isActive)}
                onClick={handleClick(route)}
              >
                <IconContainer {...iconContainerProp}>
                  <route.Icon />
                </IconContainer>
                <MainMenuLinkTitle
                  isDisabled={permissionState.isDisabled}
                  isSubscribed
                >
                  {route.name}
                </MainMenuLinkTitle>
              </LinkStyledButton>
            )}
          </LinkListItem>
        );
      })}
    </MainMenuListContainer>
  );
};
